import {
  Calendar, Globe, Award, Target, Settings,
  Users, ClipboardList, PieChart
} from 'lucide-react';

export const atAGlance = [
  { label: 'Program Name', value: 'Master of Development Project & Program Management' },
  { label: 'Duration', value: '9 Months' },
  { label: 'Mode of Delivery', value: '100% Online' },
  { label: 'Curriculum', value: '5 Courses + Capstone Field Project' },
  { label: 'Full Program Fee', value: 'USD 3,500' },
  { label: 'Scholarship', value: '90% available for Afro-Asia students' },
  { label: 'Learning Format', value: 'Flexible online study' },
  { label: 'Final Component', value: 'Capstone Field Project' },
  { label: 'Documents Issued', value: 'Master\'s Degree Certificate and Academic Transcript, subject to successful completion' },
];

export const whoIsFor = [
  'Development and NGO professionals',
  'Project and program staff',
  'MEAL, finance, grants, and operations staff',
  'Public sector and civil society professionals',
  'Social impact and community development practitioners',
  'Recent graduates seeking a practical development-sector pathway',
  'Career changers entering NGO, development, or social impact work',
  'Professionals seeking stronger knowledge of project cycles, program coordination, finance, reporting, and results management'
];

export const entryRequirements = [
  'A bachelor\'s degree or equivalent qualification',
  'Proficiency in English',
  'Interest in development project and program management',
  'Ability to study online'
];

export const whyChooseFeatures = [
  {
    icon: Calendar,
    title: 'Focused 9-Month Pathway',
    description: 'A structured 9-month postgraduate pathway focused on practical development project and program management.'
  },
  {
    icon: Settings,
    title: 'Integrated Curriculum',
    description: 'The program brings together project management, program management, MEAL, project finance, and Theory of Change in one connected learning pathway.'
  },
  {
    icon: Target,
    title: 'Applied Capstone Field Project',
    description: 'Learners complete a capstone project that connects learning with development practice, implementation challenges, stakeholder engagement, and evidence-informed decision-making.'
  },
  {
    icon: Globe,
    title: 'Flexible Online Delivery',
    description: 'The program is delivered fully online, supporting working professionals and learners across different locations.'
  },
  {
    icon: Award,
    title: 'Scholarship-Supported Access',
    description: 'Eligible applicants from Africa and Asia may benefit from the GIHDS Global Scholarship Initiative.'
  }
];

export const learningOutcomes = [
  'Plan and manage development project cycles from design to closure',
  'Design and coordinate development programs involving multiple projects',
  'Apply MEAL frameworks to support monitoring, evaluation, accountability, and learning',
  'Manage development project finance, budgeting, financial documentation, and donor compliance',
  'Use Theory of Change to design, review, and improve development interventions',
  'Navigate donor requirements, reporting standards, and stakeholder expectations',
  'Lead teams and support evidence-based decision-making in development settings',
  'Strengthen project implementation, coordination, risk management, reporting, and learning processes'
];

export const courses = [
  {
    number: '01',
    title: 'Development Project Management',
    duration: '1 Month',
    description: 'This course introduces the core concepts and practical tools required to manage development projects from identification to closure.',
    topics: [
      'Foundations, Project Identification & Definition',
      'Project Setup, Governance & Risk Management',
      'Project Planning, Scheduling & Resource Management',
      'Project Implementation, Monitoring & Control',
      'Project Closure, Sustainability & Learning'
    ]
  },
  {
    number: '02',
    title: 'Development Program Management',
    duration: '1 Month',
    description: 'This course focuses on managing development programs involving multiple projects, strategic alignment, coordination, governance, and adaptive control.',
    topics: [
      'Foundations, Program Identification & Strategic Fit',
      'Program Design, Charter & Results Architecture',
      'Program Planning, Governance & Resource Management',
      'Program Implementation, Coordination & Adaptive Control',
      'Program Closure, Sustainability & Learning'
    ]
  },
  {
    number: '03',
    title: 'Development Project Finance',
    duration: '1 Month',
    description: 'This course introduces the financial management skills required to support responsible, compliant, and effective development project delivery.',
    topics: [
      'Foundations of Development Project Finance',
      'Accounting Records, Codes & Financial Documentation',
      'Financial Planning, Budgeting & Funding Structures',
      'Financial Monitoring, Forecasting & Reporting',
      'Internal Controls, Compliance & Fraud Risk Management'
    ]
  },
  {
    number: '04',
    title: 'Development MEAL',
    duration: '1 Month',
    description: 'This course focuses on Monitoring, Evaluation, Accountability, and Learning systems used to improve program quality, accountability, evidence, and decision-making.',
    topics: [
      'Foundations, Ethics & MEAL Architecture',
      'Logic Models, Results Frameworks & Indicators',
      'MEAL Planning, Accountability & Evaluation Design',
      'MEAL Data Collection, Quality & Management',
      'MEAL Data Analysis, Reporting & Adaptive Learning'
    ]
  },
  {
    number: '05',
    title: 'Theory of Change (ToC)',
    duration: '1 Month',
    description: 'This course introduces Theory of Change as a practical tool for designing, reviewing, and improving development projects and programs.',
    topics: [
      'Theory of Change for Development Projects and Programs'
    ]
  },
  {
    number: '06',
    title: 'Capstone Field Project',
    duration: '4 Months',
    description: 'The capstone project allows learners to apply their knowledge to a development project, program, or operational challenge.',
    topics: [
      'Project identification and scoping',
      'Research methodology and data collection',
      'Stakeholder engagement and coordination',
      'Implementation and documentation',
      'Final presentation and peer review'
    ],
    isCapstone: true
  }
];

export const careerPathways = {
  projectProgram: {
    title: 'Project & Program Management',
    icon: ClipboardList,
    roles: ['Project Officer', 'Project Manager', 'Program Officer', 'Program Manager']
  },
  mealFinance: {
    title: 'MEAL, Grants & Finance',
    icon: PieChart,
    roles: ['MEAL Officer', 'Monitoring and Evaluation Officer', 'Grants Officer', 'Project Finance / Budget Officer']
  },
  leadership: {
    title: 'Coordination & Leadership',
    icon: Users,
    roles: ['Development Program Coordinator', 'Partnership Coordinator', 'NGO Program Manager', 'Development Project Coordinator']
  }
};

export const admissionsSteps = [
  { step: 1, title: 'Application Submission', description: 'Applicants complete the online application form and submit the required information.' },
  { step: 2, title: 'Application Review', description: 'The Admissions Team reviews the application and eligibility for the program and scholarship.' },
  { step: 3, title: 'Admissions Offer', description: 'Successful applicants may receive an admissions offer and scholarship confirmation.' },
  { step: 4, title: 'Tuition Payment', description: 'Applicants complete the required tuition payment to confirm admission.' },
  { step: 5, title: 'Program Begins', description: 'Confirmed students receive program access and begin their online learning pathway.' }
];

export const faqs = [
  { question: 'Is the program fully online?', answer: 'Yes. The program is delivered entirely online and is designed to be accessible to learners across different locations.' },
  { question: 'How do I apply?', answer: 'Applicants must complete the application form, submit the required documents, and undergo admission and scholarship review by the Institute.' },
  { question: 'Who is eligible for the Afro-Asia Scholarship?', answer: 'The scholarship is available to eligible applicants from Africa and Asia who are committed to development, NGO, public sector, or social impact work.' },
  { question: 'What is the full program fee?', answer: 'The full tuition for the Master of Development Project & Program Management is USD 3,500 for the entire 9-month program. Eligible Afro-Asia students may apply for our 90% scholarship.' },
  { question: 'What documents are awarded upon completion?', answer: 'Eligible graduates may receive a Master\'s Degree Certificate and Academic Transcript upon successful completion of the program and fulfilment of all academic requirements.' },
  { question: 'Can working professionals apply?', answer: 'Yes. The program is designed to support flexible online study alongside professional responsibilities.' },
  { question: 'Do I need prior project management experience to apply?', answer: 'Not necessarily. The program is open to applicants from relevant academic and professional backgrounds, including early-career professionals and career changers.' },
  { question: 'How long does the program take?', answer: 'The program is designed to be completed over 9 months.' },
  { question: 'What is the capstone project?', answer: 'The capstone project allows learners to apply their knowledge to a development project, program, or operational challenge. It includes project identification, research methodology, stakeholder engagement, implementation documentation, and final presentation.' }
];
